import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ShieldCheck, Car, Droplet, Zap, Heart, BookOpen, DollarSign, Download, LogOut, ShieldOff } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Page, Spinner } from "@/components/shared";

const TABS = ["Overview", "Impact", "Campaigns", "Reports"];

export default function BrandPortal() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");

  useEffect(() => { api.get("/brand/impact").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--cs-bg)" }}><Spinner label="Loading impact…" /></div>;

  const m = d.metrics;
  const cards = [
    { icon: Users, label: "People Supported", value: m.people_supported.toLocaleString(), color: "var(--cs-magenta)" },
    { icon: ShieldCheck, label: "Passport Engagements", value: m.passport_engagements.toLocaleString(), color: "var(--cs-green)" },
    { icon: Car, label: "Safe Ride Credits Redeemed", value: m.safe_ride_credits.toLocaleString(), color: "var(--cs-green)" },
    { icon: Droplet, label: "Hydration Redemptions", value: m.hydration_redemptions.toLocaleString(), color: "var(--cs-blue)" },
    { icon: Zap, label: "Charging Sessions", value: m.charging_sessions.toLocaleString(), color: "var(--cs-amber)" },
    { icon: Heart, label: "Wellness Kits Distributed", value: m.wellness_kits.toLocaleString(), color: "var(--cs-magenta)" },
    { icon: BookOpen, label: "Support Resources Accessed", value: m.support_resources.toLocaleString(), color: "var(--cs-blue)" },
    { icon: DollarSign, label: "Total Benefits Distributed", value: `$${m.total_benefit_value.toLocaleString()}`, color: "var(--cs-green)" },
  ];

  const download = () => {
    const rows = Object.entries(m).map(([k, v]) => `${k},${v}`).join("\n");
    const blob = new Blob([`metric,value\n${rows}`], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "brand-impact-report.csv"; a.click();
    toast.success("Impact report downloaded");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cs-bg)" }}>
      <header className="sticky top-0 z-30 flex items-center justify-between px-5 lg:px-8 py-4" style={{ background: "var(--cs-card)", borderBottom: "1px solid var(--cs-border)" }}>
        <Logo size="sm" />
        <button data-testid="brand-logout" onClick={() => { logout(); nav("/"); }} className="cs-tap flex items-center gap-2 text-sm" style={{ color: "var(--cs-red)" }}>
          <LogOut size={18} /> <span className="hidden sm:inline">Sign out</span>
        </button>
      </header>

      <main className="p-5 lg:p-8 max-w-6xl mx-auto">
        <Page>
          <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Impact Portal</p>
          <h1 className="font-head font-extrabold text-3xl">{d.brand_name}</h1>

          <div className="cs-card p-4 mt-4 flex items-start gap-3" style={{ borderColor: "var(--cs-blue)" }} data-testid="brand-privacy-notice">
            <ShieldOff size={22} style={{ color: "var(--cs-blue)" }} className="flex-shrink-0 mt-0.5" />
            <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>
              You see <span className="text-white font-semibold">aggregate impact only</span>. No participant names, locations, crew data, help requests, or incident details are ever shared.
            </p>
          </div>

          <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button key={t} data-testid={`brand-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                className="cs-tap text-sm font-semibold rounded-full px-4 py-1.5 flex-shrink-0" style={{ background: tab === t ? "var(--cs-magenta)" : "var(--cs-card)", color: "#fff", border: "1px solid var(--cs-border)" }}>{t}</button>
            ))}
          </div>

          {tab === "Overview" && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {cards.map((c) => (
                <div key={c.label} data-testid="brand-portal-aggregate-impact-chart" className="cs-card p-5">
                  <c.icon size={22} style={{ color: c.color }} />
                  <p className="font-head font-extrabold text-2xl mt-3">{c.value}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--cs-fg2)" }}>{c.label}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "Impact" && (
            <div className="grid lg:grid-cols-2 gap-5 mt-6">
              <div className="cs-card p-5">
                <h2 className="font-head font-bold text-lg mb-4">People Supported Over Time</h2>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={d.timeseries}>
                    <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E01A4F" stopOpacity={0.6} /><stop offset="100%" stopColor="#E01A4F" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="label" stroke="#A1A1AA" fontSize={12} />
                    <YAxis stroke="#A1A1AA" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#14141B", border: "1px solid #27272A", borderRadius: 12 }} />
                    <Area type="monotone" dataKey="people" stroke="#E01A4F" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="cs-card p-5">
                <h2 className="font-head font-bold text-lg mb-4">Benefits Redeemed Over Time</h2>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={d.timeseries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="label" stroke="#A1A1AA" fontSize={12} />
                    <YAxis stroke="#A1A1AA" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#14141B", border: "1px solid #27272A", borderRadius: 12 }} cursor={{ fill: "rgba(224,26,79,0.1)" }} />
                    <Bar dataKey="benefits" fill="#10B981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {tab === "Campaigns" && (
            <div className="mt-6 space-y-3">
              {d.campaigns.map((c) => {
                const pct = Math.min(100, Math.round((c.redeemed / c.funded) * 100));
                return (
                  <div key={c.name} data-testid={`brand-campaign-${c.category}`} className="cs-card p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-head font-bold text-lg">{c.name}</p>
                        <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>Sponsored by {c.sponsor}</p>
                      </div>
                      <span className="font-head font-bold" style={{ color: "var(--cs-green)" }}>{c.redeemed.toLocaleString()} / {c.funded.toLocaleString()}</span>
                    </div>
                    <div className="rounded-full overflow-hidden mt-3" style={{ height: 8, background: "var(--cs-card-hi)" }}>
                      <div className="h-full magenta-grad" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "Reports" && (
            <div className="cs-card p-6 mt-6 text-center">
              <DollarSign size={36} style={{ color: "var(--cs-green)" }} className="mx-auto" />
              <p className="font-head font-extrabold text-2xl mt-3">${m.total_benefit_value.toLocaleString()} in safety funded</p>
              <p className="text-sm mt-1" style={{ color: "var(--cs-fg2)" }}>We funded meaningful safety support — without tracking anyone.</p>
              <button data-testid="brand-download-report" onClick={download} className="cs-tap mt-5 magenta-grad rounded-xl px-6 py-3 text-white font-bold inline-flex items-center gap-2">
                <Download size={18} /> Download Campaign Impact Report
              </button>
            </div>
          )}
        </Page>
      </main>
    </div>
  );
}
